/**
 * Rest-space retargeting (plan D5 / D13).
 *
 * A canonical rotation `q_c` is expressed in the canonical world-aligned rest frame: it is the
 * rotation the bone's subtree receives relative to its rest orientation. A source bone carries a
 * rest world orientation `W_b` (`RigBinding.restRotationOffsets`, already multiplied by the
 * binding's world correction) and a rest local rotation `L_s`.
 *
 *   source local  L' = L_s * (W_b^-1 * q_c * W_b)
 *   canonical    q_c = W_b * (L_s^-1 * L') * W_b^-1
 *
 * The two are exact inverses of each other, and an identity `W_b` with an identity `L_s` makes
 * both the identity map — which is exactly what a rig already built on the canonical skeleton
 * gets. Pure tuple math: no `three` objects, no allocation when `out` is supplied.
 */

import { normalizeQuat, quatInvert, quatMultiply } from '../shared';
import type { QuaternionTuple } from '../shared';

// Module-level scratch. These functions are called once per bone per frame during playback, so
// they must not allocate; they are synchronous and never re-entrant.
const scratchA: QuaternionTuple = [0, 0, 0, 1];
const scratchB: QuaternionTuple = [0, 0, 0, 1];

/** Canonical rotation → the local rotation to write onto the source bone. */
export function canonicalToSourceLocal(
  W_b: QuaternionTuple,
  restLocal: QuaternionTuple,
  q_c: QuaternionTuple,
  out?: QuaternionTuple,
): QuaternionTuple {
  const target = out ?? ([0, 0, 0, 1] as QuaternionTuple);
  quatInvert(W_b, scratchA); // W_b^-1
  quatMultiply(scratchA, q_c, scratchB); // W_b^-1 * q_c
  quatMultiply(scratchB, W_b, scratchA); // W_b^-1 * q_c * W_b
  quatMultiply(restLocal, scratchA, target); // L_s * (…)
  return normalizeQuat(target, target);
}

/** The local rotation currently on a source bone → the canonical rotation to key. */
export function sourceLocalToCanonical(
  W_b: QuaternionTuple,
  restLocal: QuaternionTuple,
  sourceLocal: QuaternionTuple,
  out?: QuaternionTuple,
): QuaternionTuple {
  const target = out ?? ([0, 0, 0, 1] as QuaternionTuple);
  quatInvert(restLocal, scratchA); // L_s^-1
  quatMultiply(scratchA, sourceLocal, scratchB); // L_s^-1 * L'
  quatMultiply(W_b, scratchB, scratchA); // W_b * (…)
  quatInvert(W_b, scratchB); // W_b^-1
  quatMultiply(scratchA, scratchB, target); // … * W_b^-1
  return normalizeQuat(target, target);
}
